import React, { useState } from "react";
import EyeGraphic from "./EyeGraphic";
import { SELECTION_STYLE, num, overflowNotes, sagKey, sagNum } from "./scanResult";
import "./LensSpecs.css";

/**
 * The lens to order: six figures beside the lens drawn on the eye, with the
 * diameter choice above and anything the fitter must know below.
 *
 * `r` is the result with the chosen diameter laid over it (withDiameter);
 * `sim` is the result's simulated fit, used here only for the eye's own
 * landmarks -- where the limbus and the pupil are.
 */

function Box({ label, children, unit, hint }) {
  return (
    <div className="ls-box">
      <span className="ls-box-label">{label}</span>
      <span className="ls-box-value">
        {children == null ? <em className="ls-missing">—</em> : children}
        {children != null && unit ? <span className="ls-unit">{unit}</span> : null}
      </span>
      {hint ? <span className="ls-box-hint">{hint}</span> : null}
    </div>
  );
}

function sagValue(order) {
  if (!order) return null;
  const be = order["Bi-Elevation"];
  if (be) {
    return (
      <span className="ls-sag-pair">
        <span>{sagNum(be.flat_meridian_sag)}<small>flat</small></span>
        <span>{sagNum(be.steep_meridian_sag)}<small>steep</small></span>
      </span>
    );
  }
  return sagNum(order["Lens Sag"]);
}

export default function LensSpecs({ r, options, onChoose, createdAt, code, sim }) {
  const [copied, setCopied] = useState(false);
  const thin = typeof r.coverage === "number" && r.coverage < 0.995;
  const active = options && options.find((o) => o["Lens Diameter"] === r.diameter);

  const quadrantOrder = r.biElevation ? { "Bi-Elevation": r.biElevation } : { "Lens Sag": r.sag };
  const toricOrder = r.toricity && (r.toricity["Bi-Elevation"] || r.toricity["Lens Sag"] != null) ? r.toricity : null;
  const ordersDiffer = toricOrder != null && sagKey(toricOrder) !== sagKey(quadrantOrder);
  const toricAsked = r.toricity && r.toricity.requested;

  const simOption = sim && sim.version >= 2 && Array.isArray(sim.options)
    ? sim.options.find((o) => o.diameter === r.diameter) : null;
  const steps = (r.quadrants || []).map((q) => {
    const m = /^(?:Steep|Flat) (\d+) /.exec(q.axis || "");
    return m ? { centre: Number(m[1]), steps: q.steps } : null;
  }).filter(Boolean);
  const eyeSide = r.eye === "OD" || r.eye === "OS" ? r.eye : null;

  return (
    <div className="ls">
      <div className="ls-head">
        <h2 className="ls-title">Lens Specs</h2>
        <div className="ls-meta">
          {r.eye ? <span className="ls-eye">{r.eye}</span> : null}
          {code && (
            <button
              type="button"
              className="ls-code"
              title="Copy this reference"
              onClick={() => {
                if (navigator.clipboard?.writeText) {
                  navigator.clipboard.writeText(code).then(
                    () => { setCopied(true); setTimeout(() => setCopied(false), 1600); },
                    () => {}
                  );
                }
              }}
            >
              {copied ? "copied" : code}
            </button>
          )}
          <span className="ls-date">{createdAt ? new Date(createdAt).toLocaleString() : ""}</span>
        </div>
      </div>

      {options && options.length > 1 && (
        <div className="ls-dia" role="group" aria-label="Lens diameter">
          {options.map((o) => {
            const on = o["Lens Diameter"] === r.diameter;
            const kind = SELECTION_STYLE[o["Selection"]] || SELECTION_STYLE.unknown;
            return (
              <button
                key={o["Lens Diameter"]}
                type="button"
                className={`ls-dia-opt ls-dia-${kind.slug}${on ? " ls-dia-on" : ""}`}
                onClick={() => onChoose(o["Lens Diameter"])}
                aria-pressed={on}
                title={o["Selection Basis"] || ""}
              >
                <span className="ls-dia-mm">{o["Lens Diameter"].toFixed(1)}</span>
                <span className="ls-dia-tag">{kind.label}</span>
                {typeof o["Dot Clearance"] === "number" && (
                  <span className="ls-dia-dots">dots {o["Dot Clearance"] > 0 ? "+" : ""}{o["Dot Clearance"].toFixed(2)}</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      <div className="ls-body">
        <div className="ls-boxes">
          <Box label="Diameter" unit="mm">{num(r.diameter, 1)}</Box>
          <Box label={<>Sag <span className="ls-box-unit">µm</span></>}
               hint={typeof r.clearance === "number" ? `incl. ${r.clearance} µm clearance` : null}>
            {sagValue(quadrantOrder)}
          </Box>
          <Box label="Base curve" unit="mm"
               hint={r.midClearance != null ? `${r.midClearance} µm mid-peripheral clearance` : null}>
            {num(r.baseCurve, 2)}
          </Box>
          <Box label="Toricity" hint={toricAsked ? `F ${toricAsked["F steps"]} / S ${toricAsked["S steps"]} asked` : null}>
            {r.toricity ? `F ${r.toricity["F steps"]} / S ${r.toricity["S steps"]}` : null}
          </Box>
          <Box label="Flat axis">{r.rotation && r.rotation.flat != null ? `${num(r.rotation.flat, 0)}°` : null}</Box>
          <Box label="Steep axis">{r.rotation && r.rotation.steep != null ? `${num(r.rotation.steep, 0)}°` : null}</Box>
        </div>

        <div className="ls-eye-graphic">
          {typeof r.diameter === "number" && (
            <EyeGraphic
              side={eyeSide}
              diameter={r.diameter}
              lcc={active ? active["Limbal Clearance Curve"] : null}
              midperipheral={active ? active["Midperipheral Zone"] : null}
              steps={steps}
              steep={r.rotation ? r.rotation.steep : null}
              flat={r.rotation ? r.rotation.flat : null}
              limbus={sim && sim.version >= 2 ? sim.limbus : null}
              pupil={sim && sim.version >= 2 && sim.landmarks ? sim.landmarks.pupil : null}
              hwtw={r.diameterSelection ? r.diameterSelection.HVID : r.hwtw}
              dots={simOption ? simOption.dots : null}
            />
          )}
        </div>
      </div>

      <div className="ls-notes">
        {active && active["Selection Basis"] && (
          <p className={`ls-note ls-basis-${(SELECTION_STYLE[active["Selection"]] || SELECTION_STYLE.unknown).slug}`}>
            <strong>{(SELECTION_STYLE[active["Selection"]] || SELECTION_STYLE.unknown).label}</strong>
            {" — "}{active["Selection Basis"]}.
          </p>
        )}
        {r.diameterSelection && r.diameterSelection.disclaimer && (
          <p className="ls-note ls-note-warn">{r.diameterSelection.disclaimer}</p>
        )}
        {r.diameterSelection && r.diameterSelection.agreed && r.diameterSelection.agreed.length === 0 &&
          r.diameterSelection.by_drill_dots && r.diameterSelection.by_drill_dots.length === 0 &&
          r.diameterSelection.HVID != null && (
            <p className="ls-note ls-note-warn">
              No diameter places the limbal drill dots within the guide's target for an HVID of{" "}
              {r.diameterSelection.HVID.toFixed(1)} mm. Every option either sits inside the limbus or more than 0.5 mm outside it.
            </p>
        )}
        {r.recommended === false && options && (
          <p className="ls-note ls-note-warn">
            Showing an alternative to the {options.find((o) => o["Recommended"])?.["Lens Diameter"].toFixed(1)} mm the
            software selected for this eye.
          </p>
        )}
        {thin && (
          <p className="ls-note ls-note-warn">
            Only {Math.round(r.coverage * 100)}% of the sampling ring carried data at this diameter, so these figures rest on a thinner sample.
          </p>
        )}
        {ordersDiffer && (
          <p className="ls-note">
            The sag above goes with the quadrant steps on the lens. The toric order (F {r.toricity["F steps"]} / S{" "}
            {r.toricity["S steps"]}) needs {toricOrder["Bi-Elevation"]
              ? `${sagNum(toricOrder["Bi-Elevation"].flat_meridian_sag)} flat / ${sagNum(toricOrder["Bi-Elevation"].steep_meridian_sag)} steep`
              : sagNum(toricOrder["Lens Sag"])} µm: each order carries its own steps past the manufacturer's limits into the bi-elevation.
          </p>
        )}
        {r.biElevationReason && <p className="ls-note">Bi-elevation: {r.biElevationReason}.</p>}
        {overflowNotes(r.toricity && r.toricity["Step Limits"], "Toric order").map((text) => (
          <p key={text} className="ls-note ls-note-warn">{text}</p>
        ))}
        {(r.quadrants || []).filter((q) => q.limited || q.requested).map((q) => (
          <p key={q.axis} className="ls-note ls-note-warn">
            {q.axis}: {q.steps}{q.limited ? `, at the limit — ${q.requested} requested` : `, moved with the bi-elevation — ${q.requested} requested`}.
          </p>
        ))}
        {overflowNotes(r.limits, ordersDiffer ? "Quadrant-specific order" : null).map((text) => (
          <p key={text} className="ls-note ls-note-warn">{text}</p>
        ))}
        <p className="ls-note ls-note-quiet">
          Quadrant steps are 30 µm each; STD means no step.
          {r.limits && ` Maximum ${r.limits["max steep"]} steep, ${r.limits["max flat"]} flat.`}
          {" "}Drill dots are drawn evenly spaced around the limbal clearance curve.
        </p>
      </div>
    </div>
  );
}
